import { create } from 'zustand'

interface User {
  id: string
  email: string
  name: string
  role: 'admin' | 'teacher' | 'student'
  avatar?: string
  subscription: 'free' | 'premium' | 'enterprise'
  lastLogin: string
  twoFactorEnabled: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<void>
  googleLogin: (token: string) => Promise<void>
  logout: () => void
  updateUser: (data: Partial<User>) => void
  init: () => void
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://mazakertesh-api.mazakerteshleh-support.workers.dev'

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,

  // Runs once on app load to check existing token
  init: () => {
    const token = localStorage.getItem('mazakertesh_token')
    if (!token) {
      set({ isLoading: false, isAuthenticated: false })
      return
    }
    fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error('Invalid token')
        return res.json()
      })
      .then(data => {
        set({ user: data.user, token, isLoading: false, isAuthenticated: true })
      })
      .catch(() => {
        localStorage.removeItem('mazakertesh_token')
        set({ user: null, token: null, isLoading: false, isAuthenticated: false })
      })
  },

  login: async (email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)

    localStorage.setItem('mazakertesh_token', data.token)
    set({ user: data.user, token: data.token, isLoading: false, isAuthenticated: true })
  },

  register: async (name: string, email: string, password: string) => {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, password })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)

    localStorage.setItem('mazakertesh_token', data.token)
    set({ user: data.user, token: data.token, isLoading: false, isAuthenticated: true })
  },

  googleLogin: async (googleToken: string) => {
    const res = await fetch(`${API_BASE}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: googleToken })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)

    localStorage.setItem('mazakertesh_token', data.token)
    set({ user: data.user, token: data.token, isLoading: false, isAuthenticated: true })
  },

  logout: () => {
    localStorage.removeItem('mazakertesh_token')
    set({ user: null, token: null, isLoading: false, isAuthenticated: false })
    window.location.href = '/'
  },

  updateUser: (data) => {
    set((state) => ({
      user: state.user ? { ...state.user, ...data } : null
    }))
  }
}))

// Auto-check token when app loads
setTimeout(() => useAuthStore.getState().init(), 100)